/**
 * "Add" split button for the layer panel.
 *
 * The main half opens the dialog on its last-used source; the caret opens a
 * menu so the user can jump straight to browsing a file or connecting to a
 * service. Both routes land in the same dialog — there is one add flow, not two.
 *
 * The menu is PORTALLED to <body> and positioned from the button's measured
 * rect. Rendered in place it was clipped by the layer panel's `overflow-hidden`,
 * which cut the option labels in half.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, FileUp, Globe, Layers } from 'lucide-react';
import type { AddLayersMode } from './AddLayersDialog';

interface Props {
  onOpen: (mode: AddLayersMode) => void;
  /** Source the plain half of the button reopens. */
  defaultMode?: AddLayersMode;
}

const MENU_WIDTH = 288;

export default function AddLayersButton({ onOpen, defaultMode = 'server' }: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Measure after mount so the menu can be flipped/shifted at a viewport edge.
  useLayoutEffect(() => {
    if (!menuOpen) {
      setPosition(null);
      return;
    }
    const anchor = rootRef.current;
    const menu = menuRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const height = menu?.getBoundingClientRect().height ?? 120;
    const margin = 8;

    let top = rect.bottom + 6;
    if (top + height > window.innerHeight - margin) {
      top = Math.max(margin, rect.top - height - 6);
    }
    // Right-align to the button, then keep it on screen.
    const left = Math.min(
      Math.max(margin, rect.right - MENU_WIDTH),
      window.innerWidth - MENU_WIDTH - margin,
    );

    setPosition({ top, left });
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', () => setMenuOpen(false));
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const choose = (mode: AddLayersMode) => {
    setMenuOpen(false);
    onOpen(mode);
  };

  const OPTIONS: {
    mode: AddLayersMode;
    icon: typeof FileUp;
    label: string;
    hint: string;
  }[] = [
    {
      mode: 'local',
      icon: FileUp,
      label: 'Browse from this computer',
      hint: 'GeoJSON, KML/KMZ, Shapefile, CSV',
    },
    {
      mode: 'server',
      icon: Globe,
      label: 'Add from server URL',
      hint: 'GeoServer WMS or ArcGIS REST',
    },
  ];

  return (
    <div ref={rootRef} className="relative">
      <div className="flex">
        <button
          type="button"
          onClick={() => choose(defaultMode)}
          className="gl-btn gl-btn--primary rounded-r-none px-3 py-2 text-sm"
        >
          <Layers className="h-4 w-4" />
          Add
        </button>
        <button
          type="button"
          onClick={() => setMenuOpen((value) => !value)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="Choose a layer source"
          className="gl-btn gl-btn--primary rounded-l-none border-l border-white/25 px-1.5 py-2"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      {menuOpen &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label="Add layers from"
            className="gl-menu fixed z-[6000] overflow-hidden py-1"
            style={{
              width: MENU_WIDTH,
              top: position?.top ?? -9999,
              left: position?.left ?? -9999,
              // Avoid a flash at the wrong spot before measurement completes.
              visibility: position ? 'visible' : 'hidden',
            }}
          >
            {OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.mode}
                  type="button"
                  role="menuitem"
                  onClick={() => choose(option.mode)}
                  className="gl-menu-item flex w-full items-start gap-2.5 px-3 py-2.5 text-left"
                >
                  <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium text-slate-800">
                      {option.label}
                    </span>
                    <span className="block text-[11px] text-slate-400">{option.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
